import range from 'lodash.range'
import isEqual from 'lodash.isequal'
import { createSelectorCreator, defaultMemoize } from 'reselect'
import { filterCitation, getSectionCounts, filterPapersWithCitations, authorCountKey } from 'lib/helpers/filter-citation'
import { sortPapers } from 'lib/helpers/sort-citation'
import groupStringsBySimilarity from 'lib/group-strings-by-similarity'

const createSelector = createSelectorCreator(defaultMemoize, { equalityCheck: isEqual, maxSize: 20 })

const defaultCountsSort = ([, a], [, b]) => b - a

export const selectCitationsByPapers = createSelector(
  state => state.citations || state.result?.citations || [],
  state => !!state.referenceChecks,
  (citations, isRefCheck) => {
    const citationsMap = {}
    const direction = isRefCheck ? 'target' : 'source'

    for (const cite of citations) {
      const key = cite[direction]

      if (!citationsMap[key]) {
        citationsMap[key] = {
          citations: []
        }
      }

      citationsMap[key].citations.push(cite)
    }

    const result = Object.keys(citationsMap).map(source => ({
      paper: source,
      citations: citationsMap[source].citations
    }))
    return result
  }
)

export const selectSimilarSectionMap = createSelector(
  state => state.citations,
  (citations) => {
    const sectionNames = citations.map(citation => citation.section?.toLowerCase().trim() || '')
    const output = groupStringsBySimilarity(sectionNames)
    const sectionMap = citations.map(citation => {
      let section = citation.section?.toLowerCase().trim() || ''
      const generalSection = output.find(lst => lst.includes(section))
      if (generalSection?.length > 0) {
        section = generalSection.sort((a, b) => a.length - b.length)[0]
      }
      return [citation.section, section]
    })
    return Object.fromEntries(sectionMap)
  }
)

export const selectSectionCounts = createSelector(
  state => state.citations,
  state => state.references,
  state => state.filters.showReferences,
  state => state.metadata?.citingCounts?.sections,
  state => selectSimilarSectionMap(state),
  (citations, references, showReferences, sectionCounts, sectionMap) => {
    if (sectionCounts) {
      return Object.entries({
        ...sectionCounts
      }).sort(defaultCountsSort)
    }

    if (showReferences) {
      return getSectionCounts(references)
    }

    return getSectionCounts(citations, sectionMap)
  }
)

export const selectSortedFilteredCitationsByPaper = createSelector(
  selectCitationsByPapers,
  state => state.papers,
  state => state.tallies,
  state => state.citationTallies,
  state => state.referenceTallies,
  state => state.filters,
  selectSimilarSectionMap,
  (citationsByPapers, papers, tallies, citationTallies, referenceTallies, filters, sectionMap) => {
    const sort = filters.sort?.value || null
    let filteredPapers = citationsByPapers.map(({ paper, citations }) => ({
      paper: papers[paper],
      tally: citationTallies[paper] || referenceTallies[paper] || tallies[paper],
      citations: citations.filter(citation => filterCitation(citation, papers, filters, 'source', sectionMap))
    }))

    const filterCitationStatements = (
      typeof filters.withCitationStatements === 'boolean' ||
      typeof filters.withoutCitationStatements === 'boolean'
    )
    if (filterCitationStatements && !filters.withCitationStatements) {
      filteredPapers = filteredPapers.filter(
        ({ citations }) => citations.every(cite => !cite.snippet)
      )
    }
    if (filterCitationStatements && !filters.withoutCitationStatements) {
      filteredPapers = filteredPapers.filter(
        ({ citations }) => citations.filter(cite => cite.snippet).length > 0
      )
    }
    if (!sort) {
      return filteredPapers.filter(({ citations }) => citations.length > 0)
    }
    filteredPapers.sort(sortPapers(sort))
    return filteredPapers.filter(({ citations }) => citations.length > 0)
  }
)

export const selectStatementsCounts = createSelector(
  selectCitationsByPapers,
  (citationsByPapers) => ({
    withCitationStatements: citationsByPapers.filter(
      ({ citations }) => citations.filter(cite => cite.snippet).length > 0
    ).length,
    withoutCitationStatements: citationsByPapers.filter(
      ({ citations }) => citations.every(cite => !cite.snippet)
    ).length
  })
)

export const selectAuthorRelationshipCounts = createSelector(
  selectCitationsByPapers,
  state => state.referencesByPapers,
  state => state.filters.showReferences,
  state => state.metadata?.citingCounts?.relationships,
  (citationsByPapers, referencesByPaper, showReferences, relationships) => {
    if (relationships) {
      return relationships
    }

    const papers = showReferences ? referencesByPaper : citationsByPapers
    let independent = 0
    let selfCite = 0
    for (const paper of papers) {
      const selfCites = paper?.citations?.[0].selfCites
      if (selfCites?.length > 0) {
        selfCite += 1
      } else {
        independent += 1
      }
    }
    return {
      independent,
      selfCite
    }
  }
)

export const selectVisibleCitationsByPaper = createSelector(
  selectSortedFilteredCitationsByPaper,
  state => state.filters.currentPage,
  state => state.filters.citationsPerPage,
  (citationsByPaper, currentPage, citationsPerPage) => {
    const end = currentPage * citationsPerPage
    const start = end - citationsPerPage

    return citationsByPaper.slice(start, end)
  }
)

export const selectCitationsByPaperPageCount = createSelector(
  state => selectSortedFilteredCitationsByPaper(state).length,
  state => state.filters.citationsPerPage,
  (filteredCitationsByPaperCount, citationsPerPage) => Math.ceil(
    filteredCitationsByPaperCount / citationsPerPage
  )
)

export const selectMinMaxPublishedRange = createSelector(
  state => state.citations,
  state => state.papers,
  state => state.paper,
  state => state.referencesByPapers,
  state => state.filters.showReferences,
  (citations, papers, paper, references, showReferences) => {
    let years = []
    if (showReferences) {
      years = references?.map(
        reference => reference.paper?.year
      ).filter(c => !!c && c <= papers[paper]?.year) || []
    } else {
      years = citations?.map(
        c => papers[c.source]?.year
      ).filter(
        c => !!c && c >= papers[paper]?.year
      ) || []
    }

    if (years.length === 0) {
      return null
    }

    return {
      min: Math.min.apply(null, years),
      max: Math.max.apply(null, years)
    }
  }
)

export const computeYearRange = ({
  papers,
  minMaxPublishedRange
}) => {
  if (!minMaxPublishedRange?.min || !minMaxPublishedRange?.max) {
    return {}
  }

  //
  // Not sure when this scenario occurs should probably be fixed
  // upstream somewhere lol
  //
  if (minMaxPublishedRange.min === 0 || minMaxPublishedRange.max === 0) {
    return {}
  }

  const hasPapersMap = Object.values(papers).reduce((acc, next) => {
    const year = next.year
    if (year) {
      acc[year] = true
    }

    return acc
  }, {})

  //
  // Iterate over all years in range, joining buckets which do not have years in them so that
  // we can render as a continuous div of nothing
  //
  const yearRange = {}
  let countFromYear = null

  for (const year of range(minMaxPublishedRange.min, minMaxPublishedRange.max)) {
    const hasPapers = hasPapersMap[year]

    if (hasPapers) {
      countFromYear = null
    } else if (!countFromYear) {
      countFromYear = year
    }

    if (countFromYear && yearRange[countFromYear]) {
      yearRange[countFromYear].width += 1
      continue
    }

    yearRange[year] = {
      supporting: 0,
      mentioning: 0,
      contradicting: 0,
      total: 0,
      width: 1
    }
  }

  return yearRange
}

export const computeYearBins = ({
  showReferences,
  citations,
  papers,
  minMaxPublishedRange,
  yearRange
}) => citations.reduce(
  (acc, next) => {
    const source = papers[showReferences ? next.target : next.source]
    if (!source) {
      return acc
    }

    const year = source.year
    // if a reference is greater than or a citation less than the published range, skip it.
    if (
      (showReferences && year > minMaxPublishedRange?.max) ||
      (!showReferences && (year < minMaxPublishedRange?.min))
    ) {
      return acc
    }

    if (year) {
      if (!(year in acc)) {
        acc[year] = {
          supporting: 0,
          mentioning: 0,
          contradicting: 0,
          total: 0
        }
      }
      acc[year].total += 1
      acc[year][next?.type || 'mentioning'] += 1
    }
    return acc
  }, yearRange)

export const selectYearBins = createSelector(
  selectMinMaxPublishedRange,
  state => state.filters.showReferences,
  state => state.references,
  state => state.citations,
  state => state.papers,
  state => state.referencePapers,
  state => state.metadata?.citingCounts?.dateHistogram,
  (
    minMaxPublishedRange,
    showReferences,
    references,
    citations,
    papers,
    referencePapers,
    dateHistogram
  ) => {
    if (dateHistogram) {
      return dateHistogram
    }

    const yearRange = computeYearRange({ papers: showReferences ? referencePapers : papers, minMaxPublishedRange })
    return computeYearBins({
      showReferences,
      citations: showReferences ? references : citations,
      papers: showReferences ? referencePapers : papers,
      minMaxPublishedRange,
      yearRange
    })
  }
)

export const selectSortedFilteredReferencesByPaper = createSelector(
  state => state.referencesByPapers,
  state => state.referencePapers,
  state => state.filters,
  (referencesByPaper = [], papers = [], filters = {}) => {
    const sort = filters.sort?.value || null
    const filteredPapers = referencesByPaper.map(({ paper, citations, tally }) => ({
      paper: paper,
      tally: tally,
      citations: citations.filter(citation => filterCitation(citation, papers, filters, 'target'))
    }))

    if (!sort) {
      return [...filteredPapers].filter(papersWithCitations => filterPapersWithCitations(papersWithCitations, filters))
    }
    return [...filteredPapers].filter(papersWithCitations => filterPapersWithCitations(papersWithCitations, filters)).sort(sortPapers(sort))
  }
)

export const selectVisibleReferencesByPaper = createSelector(
  selectSortedFilteredReferencesByPaper,
  state => state.filters.currentPage,
  state => state.filters.citationsPerPage,
  (referencesByPaper, currentPage, citationsPerPage) => {
    const end = currentPage * citationsPerPage
    const start = end - citationsPerPage

    return referencesByPaper.slice(start, end)
  }
)

export const selectReferencesByPaperPageCount = createSelector(
  state => selectSortedFilteredReferencesByPaper(state).length,
  state => state.filters.citationsPerPage,
  (filteredReferencesByPaperCount, citationsPerPage) => Math.ceil(
    filteredReferencesByPaperCount / citationsPerPage
  )
)

export const selectFilteredCitationPapers = createSelector(
  selectSortedFilteredCitationsByPaper,
  (citationsByPaper) => citationsByPaper.length
)

export const selectFilteredCitationStatements = createSelector(
  selectSortedFilteredCitationsByPaper,
  citationsByPaper => {
    return citationsByPaper.reduce(
      (acc, next) => acc + next.citations.filter(cite => !!cite.snippet).length, 0
    )
  }
)

export const selectFilteredReferencePaperCount = createSelector(
  state => selectSortedFilteredReferencesByPaper(state),
  (referencesByPaper) => referencesByPaper.length
)

export const selectFilteredReferenceStatementsCount = createSelector(
  state => selectSortedFilteredReferencesByPaper(state),
  (referencesByPaper) => referencesByPaper.reduce(
    (acc, next) => acc + next.citations.filter(cite => !!cite.type).length, 0
  )
)

export const selectTotalReferenceStatementsCount = createSelector(
  state => state.referencesByPapers,
  (referencesByPapers) => referencesByPapers.reduce(
    (acc, next) => acc + next.citations.filter(cite => !!cite.type).length, 0
  )
)

export const selectHasMath = createSelector(
  selectVisibleCitationsByPaper,
  state => state.papers?.[state.paper] || {},
  (citations, paper) => {
    const title = paper.title || ''
    const abstract = paper.abstract || ''
    let snippets = ''
    for (const source of citations) {
      for (const cite of (source.citations || [])) {
        snippets += cite.snippet
      }
    }
    return /\$.+\$/.test(title + abstract + snippets)
  }
)

const _reduceToCounts = (papers, collection, keysFn, preCalculated = null, sortFn = defaultCountsSort) => {
  const agg = preCalculated || (collection || [])
    .reduce((acc, next) => {
      const nextDoi = next.paper.doi || next.paper
      const nextPaper = papers[nextDoi]
      const keys = keysFn(nextPaper)
      for (const key of keys) {
        acc[key] = (acc[key] || 0) + 1
      }
      return acc
    }, {})

  return Object.entries(agg)
    .sort(sortFn)
    .filter(([key]) => !!key)
}

const _reduceToJournalsCounts = (papers, collection, journals) => _reduceToCounts(
  papers,
  collection,
  paper => paper?.journal ? [paper.journal] : [],
  journals
)

const _reduceToPubtypeCounts = (papers, collection, pubTypes) => _reduceToCounts(
  papers,
  collection,
  paper => paper?.normalizedTypes || [],
  pubTypes
)

const _reduceToAuthorCounts = (papers, collection, authors) => _reduceToCounts(
  papers,
  collection,
  paper => (paper?.authors || [])
    .filter(author => author?.family || author?.given)
    .map(authorCountKey),
  authors
)

export const selectReferencesJournalsCount = createSelector(
  state => state.referencePapers,
  state => state.referencesByPapers,
  _reduceToJournalsCounts
)

export const selectCitationsJournalsCount = createSelector(
  state => state.papers,
  selectCitationsByPapers,
  state => state.metadata?.citingCounts?.journals,
  _reduceToJournalsCounts
)

export const selectReferencesAuthorsCount = createSelector(
  state => state.referencePapers,
  state => state.referencesByPapers,
  _reduceToAuthorCounts
)

export const selectCitationsAuthorsCount = createSelector(
  state => state.papers,
  selectCitationsByPapers,
  state => state.metadata?.citingCounts?.authors,
  _reduceToAuthorCounts
)

export const selectReferencesPubtypesCount = createSelector(
  state => state.referencePapers,
  state => state.referencesByPapers,
  _reduceToPubtypeCounts
)

export const selectCitationsPubtypesCount = createSelector(
  state => state.papers,
  selectCitationsByPapers,
  state => state.metadata?.citingCounts?.pubTypes,
  _reduceToPubtypeCounts
)
